import type { ApiConfig } from '@de/types/config';
import type { InterpolatedRequest } from '../data-sources/fetchDataSource';
import { OFFERINGS_SEARCH_PATH } from './offeringsResultsSourceIdentity';

const PAGING_INTEGER_KEYS = ['offset', 'limit'] as const;
const WHOLE_NUMBER = /^(?:0|[1-9]\d*)$/;

/**
 * Build a post-interpolation request transform that sends Offerings
 * search `offset` and `limit` as integers.
 *
 * Form state values are strings, and `POST /v1/offerings/search`
 * validates those two fields with `@IsInt`. The rule is top-level and
 * search-path only: a nested `limit`, `/search/match`, and every other
 * origin keep the value the author wrote.
 *
 * Returns `undefined` when no Offerings origin is configured.
 */
export function buildOfferingsPagingBodyCoercer(
  apiConfig: ApiConfig,
): ((req: InterpolatedRequest) => InterpolatedRequest) | undefined {
  const offeringsOrigins = new Set(
    [apiConfig.offeringsServiceBaseUrl, apiConfig.offeringsSearchApiUrl]
      .map(extractOrigin)
      .filter((origin): origin is string => origin !== null),
  );
  if (offeringsOrigins.size === 0) return undefined;

  return (req) => {
    if (req.method === 'GET') return req;
    if (pathnameOf(req.endpoint) !== OFFERINGS_SEARCH_PATH) return req;
    const reqOrigin = extractOrigin(req.endpoint);
    if (reqOrigin && !offeringsOrigins.has(reqOrigin)) return req;
    const coercedBody = coerceOfferingsPaging(req.body);
    if (coercedBody === req.body) return req;
    return { ...req, body: coercedBody };
  };
}

function coerceOfferingsPaging(body: unknown): unknown {
  if (!isPlainObject(body)) return body;
  let next: Record<string, unknown> | null = null;
  for (const key of PAGING_INTEGER_KEYS) {
    if (!(key in body)) continue;
    const coerced = coerceWholeNumber(body[key]);
    if (coerced === body[key]) continue;
    next ??= { ...body };
    next[key] = coerced;
  }
  return next ?? body;
}

function coerceWholeNumber(value: unknown): unknown {
  if (typeof value !== 'string') return value;
  const trimmed = value.trim();
  if (!WHOLE_NUMBER.test(trimmed)) return value;
  const parsed = Number(trimmed);
  if (!Number.isSafeInteger(parsed)) return value;
  return parsed;
}

function pathnameOf(endpoint: string): string {
  const path = endpoint.startsWith('/')
    ? (endpoint.split(/[?#]/)[0] ?? '')
    : (() => {
        try {
          return new URL(endpoint).pathname;
        } catch {
          return '';
        }
      })();
  return path.length > 1 && path.endsWith('/') ? path.slice(0, -1) : path;
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function extractOrigin(value: string | undefined): string | null {
  if (!value) return null;
  try {
    const parsed = new URL(value);
    if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') {
      return null;
    }
    return parsed.origin.toLowerCase();
  } catch {
    return null;
  }
}
